import { createAsyncThunk } from '@reduxjs/toolkit'
import { useGraphCacheStore } from '@tunasong/graph-cache'
import type { StoredObject } from '@tunasong/models'
import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { isAudio } from '@tunasong/schemas'
import invariant from 'tiny-invariant'
import { entitiesApi } from '../../api/entities.js'
import { mediaApi } from '../../api/media.js'
import { storageApi } from '../../api/storage.js'
import type { RootState } from '../../configure-store.js'
import type { TunaThunkDispatch } from '../../hooks/thunk-dispatch.hook.js'
import { createObjectPath } from '../../util.js'
import { notificationsSlice } from '../notifications/notifications-slice.js'

const mimeLoader = import('./mime-types.js')

interface UploadResult {
  filename: string
}

/** Upload the blob to storage. */
export const uploadBlob = createAsyncThunk<
  UploadResult,
  { entityId: string; name: string; blob: Blob; defaultMimeType?: string; metadata?: Record<string, string> },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'storage/uploadBlob',
  async (
    { entityId, name, blob, defaultMimeType = 'audio/wav', metadata },
    { rejectWithValue, getState, dispatch }
  ) => {
    const userId = getState().user.userId

    invariant(userId, 'User must be logged in to upload a file')

    /** Upload the actual file */
    const mime = await mimeLoader
    const mimeType = (blob.type ? blob.type : mime.lookup(name)) || defaultMimeType
    const data = await blob.arrayBuffer()

    const filename = createObjectPath({ entityId, userId })
    /**
     * SHA256 signature required
     * @see https://github.com/brix/crypto-js/issues/91
     * @see https://github.com/brix/crypto-js
     * @see https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-s3.html#private-content-origin-access-identity-signature-version-4
     */

    // await API.upload(token, filename, data, contentType)

    /** Get the upload URL */
    const {
      data: signedUrl,
      isError,
      error,
    } = await dispatch(storageApi.endpoints.getUploadUrl.initiate({ filename, contentType: mimeType, metadata }))

    if (isError || !signedUrl) {
      console.log("Couldn't get signed URL", error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const message = (error as any).data?.message ?? 'Unable to authorize upload of file'
      dispatch(
        notificationsSlice.actions.setAlert({
          severity: 'error',
          title: 'Upload failed',
          message: `Unable to authorize upload of file: ${message ? message : 'Unknown error'}`,
        })
      )
      return rejectWithValue('Unable to authorize upload of file')
    }

    /** Upload the actual file. If the upload fails, the promise should be rejected */
    await dispatch(
      storageApi.endpoints.uploadFile.initiate({ signedUrl: signedUrl.url, data, contentType: mimeType })
    ).unwrap()

    return { filename }
  }
)

export const uploadStoredEntity = createAsyncThunk<
  Persisted<Entity>,
  {
    id: string
    name: string
    type: EntityType
    parentId: string | null
    blob: Blob
    message?: string
    isPrivate?: boolean
    tags?: string[]
  },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'storage/uploadStoredEntity',
  async (
    { id, name, parentId, type, blob, message = 'Uploading media...', isPrivate = false, tags },
    { getState, dispatch }
  ) => {
    const userId = getState().user.userId
    if (!userId) {
      throw new Error(`Upload requires an authenticated user`)
    }
    if (!blob.type) {
      throw new Error("Blob must have a valid 'type' property")
    }
    invariant(type, 'Entity type must be provided')

    dispatch(notificationsSlice.actions.setAlert({ severity: 'info', message }))

    /** We create the id client side, because we need to tag the blob with it */
    const metadata = { entityId: id }

    const { filename } = await dispatch(uploadBlob({ entityId: id, name, blob, metadata })).unwrap()

    const storedObject = {
      id,
      name,
      type,
      parentId: parentId ? parentId : undefined,
      tags,
      storage: {
        type: 'S3',
        filename,
      },
    } satisfies StoredObject<Entity>

    const { upsertCache } = useGraphCacheStore.getState()
    // Update the cache optimistically
    const tempCachedEntity = {
      ...storedObject,
      userId,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    }

    upsertCache(tempCachedEntity)

    // Load the parent
    const parent = parentId
      ? await dispatch(entitiesApi.endpoints.loadEntity.initiate({ id: parentId })).unwrap()
      : null

    const entity = { ...storedObject, parentId: parentId ?? undefined }
    const result = await dispatch(entitiesApi.endpoints.createEntity.initiate({ entity, parent, isPrivate })).unwrap()
    /** Now the S3 object is available */
    if (isAudio(result)) {
      await dispatch(mediaApi.endpoints.analyzeAudio.initiate(result))
    }
    return result
  }
)
